import React from 'react';
import { motion } from 'framer-motion';
import { PageBanner } from '../components/PageBanner';
import { BookingCTA } from '../components/BookingCTA';
import { Button } from '../components/ui/Button';
import { SparklesIcon, HeartIcon, DollarSignIcon, DoorOpenIcon, MapPinIcon, PhoneIcon } from 'lucide-react';
const features = [{
  icon: SparklesIcon,
  title: 'Expert Stylists',
  description: 'Years of experience in African hair braiding with a passion for creating beautiful, protective styles.'
}, {
  icon: HeartIcon,
  title: 'Premium Products',
  description: 'We use only the best natural hair care products to ensure healthy, long-lasting styles.'
}, {
  icon: DoorOpenIcon,
  title: 'Walk-ins Welcome',
  description: "No appointment needed! Come anytime during our business hours and we'll take care of you."
}, {
  icon: DollarSignIcon,
  title: 'Affordable Pricing',
  description: 'Quality styles at competitive prices. Plus, enjoy our ongoing promotions and discounts.'
}];
export function AboutPage() {
  return <>
      <PageBanner title="About Us" subtitle="Honoring tradition while elevating modern style" backgroundImage="https://cdn.magicpatterns.com/uploads/dWCkh2pKNY8WmbWaAv4P5x/ChatGPT_Image_Mar_10,_2026,_11_45_41_AM.png" data-id="element-496" />

      {/* Who We Are Section */}
      <section className="py-8 md:py-20 bg-white" data-id="element-497">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-498">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center" data-id="element-499">
            <span className="text-burgundy font-medium tracking-[0.2em] uppercase text-sm mb-4 block" data-id="element-500">
              Who We Are
            </span>
            <h2 className="text-4xl md:text-5xl font-heading font-bold text-jet mb-8" data-id="element-501">
              Welcome to Rozii African Hair Braiding
            </h2>
            <div className="space-y-6 text-jet/70 text-lg leading-relaxed" data-id="element-502">
              <p data-id="element-503">
                Step into a space where cultural heritage meets contemporary
                elegance. Located at 7375 Executive Place, Suite 214, Lanham MD,
                Rozii African Hair Braiding is a sanctuary dedicated to the
                ancient, beautiful art of protective styling.
              </p>
              <p data-id="element-504">
                We take immense pride in our roots, seamlessly blending
                generations of traditional braiding techniques with modern
                trends to create styles that are as unique as you are. Our
                skilled stylists are passionate about celebrating your
                individuality, working meticulously to enhance your natural
                beauty.
              </p>
              <p data-id="element-505">
                Whether you're looking for classic box braids, trendy knotless
                braids, elegant locs, or any protective style, our team is here
                to bring your vision to life. We believe in creating a relaxing,
                soothing, and refreshing salon experience for every client.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-8 md:py-20 bg-beige" data-id="element-506">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-507">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-16" data-id="element-508">
            <span className="text-burgundy font-medium tracking-[0.2em] uppercase text-sm mb-4 block" data-id="element-509">
              Why Choose Us
            </span>
            <h2 className="text-4xl md:text-5xl font-heading font-bold text-jet" data-id="element-510">
              The Rozii Difference
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-511">
            {features.map((feature, index) => <motion.div key={feature.title} initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} className="bg-white p-8 rounded-2xl shadow-sm text-center" data-id="element-512">
                <div className="w-16 h-16 bg-golden/10 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-513">
                  <feature.icon className="w-8 h-8 text-golden" data-id="element-514" />
                </div>
                <h3 className="text-xl font-heading font-bold text-jet mb-3" data-id="element-515">
                  {feature.title}
                </h3>
                <p className="text-jet/60 text-sm leading-relaxed" data-id="element-516">
                  {feature.description}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Call CTA Section */}
      <section className="py-8 md:py-20 bg-white" data-id="element-517">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-518">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="bg-jet rounded-2xl overflow-hidden shadow-2xl flex flex-col md:flex-row" data-id="element-519">
            <div className="w-full md:w-1/2 aspect-square md:aspect-auto" data-id="element-520">
              <img src="https://cdn.magicpatterns.com/uploads/8Wg9wbxDuh49g9nkLKA4TN/high-quality_image_of_a_woman_with_long,_neat_braids,_styled_elegantly._She_is_seated_in_a_confident_pose,_looking_slightly_over_her_shoulder._The_lighting_highlights_her_smooth_skin_and_detailed_braiding,_reinf_(3).jpg" alt="Beautiful braids" className="w-full h-full object-cover" data-id="element-521" />
            </div>
            <div className="w-full md:w-1/2 p-10 md:p-16 flex flex-col justify-center items-start text-left" data-id="element-522">
              <span className="text-brand-gold font-medium tracking-[0.2em] uppercase text-sm mb-4 block" data-id="element-523">
                Ready for a change?
              </span>
              <h2 className="text-4xl md:text-5xl font-heading font-bold text-white mb-6" data-id="element-524">
                Book Your Next Style
              </h2>
              <p className="text-white/70 text-lg mb-10 leading-relaxed" data-id="element-525">
                Our expert braiders are ready to give you the crown you deserve.
                Call us today to secure your appointment or ask any questions
                about our services.
              </p>
              <a href="tel:2409217857" className="inline-flex items-center justify-center px-8 py-4 bg-brand-gold text-white font-bold uppercase tracking-widest text-sm rounded-full hover:bg-white hover:text-jet transition-colors shadow-lg" data-id="element-526">
                <PhoneIcon className="w-5 h-5 mr-2" data-id="element-527" />
                Call (240) 921-7857
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="py-8 md:py-20 bg-beige" data-id="element-528">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-529">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-12" data-id="element-530">
            <span className="text-burgundy font-medium tracking-[0.2em] uppercase text-sm mb-4 block" data-id="element-531">
              Our Work
            </span>
            <h2 className="text-4xl md:text-5xl font-heading font-bold text-jet" data-id="element-532">
              Gallery
            </h2>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-533">
            {['https://cdn.magicpatterns.com/uploads/oWB37Cf5isF4iXL9cmjzC1/unnamed_(3).webp', 'https://cdn.magicpatterns.com/uploads/uZPGDAXUcxv2h2PRxGnT3C/unnamed_(2).webp', 'https://cdn.magicpatterns.com/uploads/9io7yx3BmLPZQwjKLXQQc9/unnamed_(1).webp', 'https://cdn.magicpatterns.com/uploads/s6Lf7SojeoCY8cGPB5rJ3J/unnamed.webp', 'https://cdn.magicpatterns.com/uploads/j8Dnr4XNVo2u5NFxYqF5Vz/Screenshot_2026-03-19_160206.png', 'https://cdn.magicpatterns.com/uploads/rybf9qXPeuuwtJct9pBWR4/Screenshot_2026-03-19_160143.png', 'https://cdn.magicpatterns.com/uploads/xae55UqHqhG4f2wHghEvQ2/Screenshot_2026-03-19_160128.png', 'https://cdn.magicpatterns.com/uploads/uq1gzwAqqgzrfnhA3BwdFd/Screenshot_2026-03-19_160114.png'].map((src, index) => <motion.div key={index} initial={{
            opacity: 0,
            scale: 0.9
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} className="aspect-square rounded-2xl overflow-hidden shadow-md" data-id="element-534">
                <img src={src} alt="Rozii Hair Braiding Gallery" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" data-id="element-535" />
              </motion.div>)}
          </div>
        </div>
      </section>

      <BookingCTA data-id="element-536" />
    </>;
}